import React, { useEffect, useState } from "react";
import { Box, Button, CircularProgress, Dialog, Grid, TextField, Typography } from "@mui/material";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import { UploadCard, mergeImages } from "./CustomerDetailsStep";
import { extractAadharData, extractAadharDataFromBothSides, isValidAadharNumber, PdfPasswordRequiredError } from "../utils/aadharOcr";
import { isPdfFile, pdfFileToImageFile } from "../utils/pdfToImages";
import { shortLabel } from "../utils/policyParts";
import { canRunNow, insurerOf, stageOf, stageParts } from "../utils/relianceStages";
import { ToastError } from "../../../../UiComponents/Toaster/Toast";
import { UpdateKycFiles } from "../../../../Service/_index";

const NAVY = "#1E3A5F";
const FONT = "Inter, Poppins, sans-serif";
const btn = { textTransform: "none", borderRadius: "8px", fontFamily: FONT, fontWeight: 600, fontSize: "13px", boxShadow: "none" };
const MAX_BYTES = 5 * 1024 * 1024;

// index.scss makes every outlined input border transparent: restated here so each box is visibly a box
const boxedField = {
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff", borderRadius: "8px" },
  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": { borderColor: "#D0D5DD !important" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#98A2B3 !important" },
  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#1E3A5F !important", borderWidth: "2px !important" },
  "& .MuiOutlinedInput-root.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: "#B42318 !important" },
};

const isRelianceName = (c) => /reliance/i.test(String(c || ""));
const isKshemaName = (c) => /kshema/i.test(String(c || ""));

/** The insurers of a policy (its staged parts, else its own company). */
const companiesOf = (policy) => {
  const fromParts = stageParts(policy && policy.relianceStageView).map((p) => p.company);
  return fromParts.length ? fromParts : [policy && (policy.company || policy.Companyname)];
};

/** Which parts redo their KYC with the new files: not completed, not paid from the wallet. */
export const kycRedoParts = (policy) =>
  stageParts(policy && policy.relianceStageView).filter((p) => !p.walletPaid && stageOf(p, "kyc") !== "completed");

/** One picked file as an image (a PDF becomes a JPEG, as in the policy form), or null with a toast. */
const readPicked = async (picked) => {
  if (!picked) return null;
  const okType = ["image/jpeg", "image/jpg", "image/png", "application/pdf"].includes(picked.type) || isPdfFile(picked);
  if (!okType) {
    ToastError("Please upload only JPG, PNG or PDF files");
    return null;
  }
  if (picked.size > MAX_BYTES) {
    ToastError("File size should not exceed 5MB");
    return null;
  }
  if (!isPdfFile(picked)) return picked;
  try {
    return await pdfFileToImageFile(picked);
  } catch (error) {
    if (error instanceof PdfPasswordRequiredError) {
      const entered = window.prompt("This PDF is password protected.\n\nEnter its password (usually the first 4 letters of the name in CAPITALS followed by the year of birth, e.g. RAME1990):");
      if (!entered) {
        ToastError("PDF needs a password to be read.");
        return null;
      }
      try {
        return await pdfFileToImageFile(picked, { password: entered });
      } catch (retryError) {
        ToastError(`Could not open the PDF: ${retryError.message}`);
        return null;
      }
    }
    ToastError(`Could not read the PDF: ${error.message}`);
    return null;
  }
};

const dataUrlOf = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.readAsDataURL(file);
  });

/**
 * Edit -> KYC files (2026-10-19): give the policy a new Aadhaar / PAN and redo ONLY the KYC. The proposal is kept,
 * nothing is charged again and nothing else is redone; a failed KYC goes back to pending and runs now
 * ("Update & run") or when the automation is free ("Only update"). Reliance takes the Aadhaar front and back
 * (merged into one image, as in the policy form); KSHEMA takes no PAN.
 */
const KycFilesDialog = ({ policy, onClose, onChanged }) => {
  const open = !!policy;
  const companies = open ? companiesOf(policy) : [];
  const separate = companies.some(isRelianceName);
  const showPan = !companies.every(isKshemaName);
  const redo = open ? kycRedoParts(policy) : [];
  const hasSteps = open && stageParts(policy.relianceStageView).length > 0;
  const runnable = open && canRunNow(policy.jobStatus || policy.status);

  const [front, setFront] = useState(null); // { file, preview }
  const [back, setBack] = useState(null);
  const [single, setSingle] = useState(null);
  const [pan, setPan] = useState(null);
  const [merged, setMerged] = useState(null);
  const [aadharNumber, setAadharNumber] = useState("");
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFront(null);
    setBack(null);
    setSingle(null);
    setPan(null);
    setMerged(null);
    setAadharNumber("");
  }, [policy]);

  // Reliance: the two sides become one image (what is stored) and the number is read off both
  useEffect(() => {
    let alive = true;
    if (!separate || !front || !back) {
      setMerged(null);
      return undefined;
    }
    (async () => {
      try {
        const blob = await mergeImages(front.preview, back.preview);
        if (alive) setMerged(new File([blob], "Aadhar Card_merged.jpg", { type: "image/jpeg" }));
      } catch (e) {
        ToastError("Failed to merge the Aadhaar images");
      }
      setReading(true);
      try {
        const data = await extractAadharDataFromBothSides(front.file, back.file);
        if (alive && data && data.aadharNumber) setAadharNumber(String(data.aadharNumber).replace(/\D/g, ""));
      } catch (e) {
        /* the number can be typed */
      } finally {
        if (alive) setReading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [separate, front, back]);

  if (!open) return null;

  const pickInto = (setter, { readNumber = false } = {}) => async (event) => {
    const file = await readPicked(event && event.target && event.target.files && event.target.files[0]);
    if (!file) return;
    const preview = await dataUrlOf(file);
    setter({ file, preview });
    if (readNumber) {
      setReading(true);
      try {
        const data = await extractAadharData(file, "both");
        if (data && data.aadharNumber) setAadharNumber(String(data.aadharNumber).replace(/\D/g, ""));
      } catch (e) {
        /* the number can be typed */
      } finally {
        setReading(false);
      }
    }
  };

  const aadharFile = separate ? merged : single && single.file;
  const halfAadhar = separate && !!(front || back) && !(front && back);
  const numberOk = !aadharNumber || isValidAadharNumber(aadharNumber);
  const hasNew = !!(aadharFile || (pan && pan.file));
  const canSave = hasNew && !halfAadhar && numberOk && !busy && !reading;
  const name = policy.fullName || policy.firstName || policy.policyId || "This policy";
  const current = policy.aadharCard && typeof policy.aadharCard === "object" ? policy.aadharCard : null;
  const currentUrl = current ? current.presignedUrl || current.location || null : null;

  const save = async (run) => {
    if (!canSave) return;
    setBusy(true);
    try {
      const res = await UpdateKycFiles(policy._id || policy.id, {
        aadharCard: aadharFile || null,
        panCard: (pan && pan.file) || null,
        aadharNumber: aadharFile ? aadharNumber : "",
        run: !!run,
      });
      if (res && !res.error) {
        if (onChanged) onChanged(res.data);
        onClose();
      }
    } finally {
      setBusy(false);
    }
  };

  const partWords = (p) => {
    const row = (p.stages || []).find((s) => s.stage === "proposal") || {};
    const kept = stageOf(p, "proposal") === "completed";
    return `${insurerOf(p)} ${p.label || shortLabel(p.key)} - ${kept ? `the proposal${row.proposalNumber ? ` ${row.proposalNumber}` : ""} is kept, only the KYC runs again` : "the Proposal is not done yet, so the run starts from the Proposal"}`;
  };

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: "14px", m: { xs: 1.5, sm: 4 }, width: { xs: "calc(100% - 24px)", sm: undefined } } }}>
      <Box sx={{ p: { xs: 2, sm: 2.5 } }} data-testid="kyc-files-dialog">
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
          <Box sx={{ width: 38, height: 38, borderRadius: "11px", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: NAVY, backgroundColor: "#EEF3F9", border: "1px solid #C9D7E8" }}>
            <BadgeOutlinedIcon />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: "15px", color: NAVY, fontFamily: FONT, lineHeight: 1.3 }}>New KYC files</Typography>
            <Typography sx={{ fontSize: "12.5px", color: "#475467", fontFamily: FONT }}>{name} · only the KYC is done again</Typography>
          </Box>
        </Box>

        <Grid container spacing={1.5} alignItems="stretch">
          {separate ? (
            <>
              <Grid item xs={12} sm={6}>
                <UploadCard title="Aadhaar Front" required file={front && front.file} preview={front && front.preview} disabled={busy} onSelect={pickInto(setFront)} onRemove={() => setFront(null)} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <UploadCard title="Aadhaar Back" required file={back && back.file} preview={back && back.preview} disabled={busy} onSelect={pickInto(setBack)} onRemove={() => setBack(null)} />
              </Grid>
            </>
          ) : (
            <Grid item xs={12} sm={6}>
              <UploadCard title="Aadhaar Card" file={single && single.file} preview={single && single.preview} disabled={busy} onSelect={pickInto(setSingle, { readNumber: true })} onRemove={() => setSingle(null)} />
            </Grid>
          )}
          {showPan ? (
            <Grid item xs={12} sm={6}>
              <UploadCard title="PAN Card" file={pan && pan.file} preview={pan && pan.preview} disabled={busy} onSelect={pickInto(setPan)} onRemove={() => setPan(null)} />
            </Grid>
          ) : null}
        </Grid>

        {current ? (
          <Typography data-testid="kyc-current-file" sx={{ mt: 1.25, fontSize: "12px", color: "#475467", fontFamily: FONT, wordBreak: "break-word" }}>
            Aadhaar now on the policy -{" "}
            {currentUrl ? (
              <Box component="a" href={currentUrl} target="_blank" rel="noopener noreferrer" sx={{ color: "#175CD3", fontWeight: 600 }}>
                {current.fileName || "open it"}
              </Box>
            ) : (
              <b>{current.fileName || "saved"}</b>
            )}
          </Typography>
        ) : null}

        {aadharFile || reading ? (
          <Box sx={{ mt: 1.5, display: "flex", alignItems: "center", gap: 1 }}>
            {reading ? <CircularProgress size={16} sx={{ color: NAVY }} /> : null}
            <TextField
              size="small"
              label="Aadhaar number (read from the new file)"
              value={aadharNumber}
              disabled={busy || reading}
              onChange={(e) => setAadharNumber(e.target.value.replace(/\D/g, "").slice(0, 12))}
              error={!numberOk}
              helperText={!numberOk ? "12 digits" : reading ? "Reading the Aadhaar…" : " "}
              inputProps={{ "data-testid": "kyc-aadhaar-number" }}
              sx={{ ...boxedField, flex: 1, "& .MuiInputBase-input": { fontFamily: FONT, fontSize: "13px" } }}
            />
          </Box>
        ) : null}
        {halfAadhar ? (
          <Typography sx={{ mt: 1, fontSize: "12px", color: "#B54708", fontFamily: FONT }}>Upload both sides of the Aadhaar.</Typography>
        ) : null}

        <Box data-testid="kyc-files-plan" sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: "10px", backgroundColor: "#EFF8FF", border: "1px solid #B2DDFF" }}>
          {redo.length ? (
            redo.map((p) => (
              <Typography key={p.key} sx={{ fontSize: "12.5px", color: "#175CD3", fontFamily: FONT, lineHeight: 1.6 }}>
                {partWords(p)}
              </Typography>
            ))
          ) : (
            <Typography sx={{ fontSize: "12.5px", color: "#175CD3", fontFamily: FONT }}>
              {hasSteps ? "The KYC is already completed - the files are only saved." : "The files are saved; the next run uses them."}
            </Typography>
          )}
          <Typography sx={{ fontSize: "12px", color: "#475467", fontFamily: FONT, mt: 0.5 }}>No new proposal and nothing is charged again.</Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end", mt: 2, flexWrap: "wrap" }}>
          <Button onClick={onClose} disabled={busy} sx={{ ...btn, color: "#475467" }}>
            Cancel
          </Button>
          {redo.length ? (
            <>
              <Button variant="outlined" disabled={!canSave} onClick={() => save(false)} data-testid="kyc-only-update" sx={{ ...btn, color: NAVY, borderColor: "#C9D7E8" }}>
                Only update
              </Button>
              {runnable ? (
                <Button variant="contained" disabled={!canSave} onClick={() => save(true)} data-testid="kyc-update-run" sx={{ ...btn, backgroundColor: NAVY }}>
                  {busy ? "Saving…" : "Update & run"}
                </Button>
              ) : null}
            </>
          ) : (
            <Button variant="contained" disabled={!canSave} onClick={() => save(false)} data-testid="kyc-save" sx={{ ...btn, backgroundColor: NAVY }}>
              {busy ? "Saving…" : "Save files"}
            </Button>
          )}
        </Box>
      </Box>
    </Dialog>
  );
};

export default KycFilesDialog;
